<!-- Main Top-Up Modal -->
<ion-modal
  [isOpen]="isModalOpen()"
  (willDismiss)="onModalDismiss()"
  [expandToScroll]="false"
  cssClass="rounded-dialog"
  [breakpoints]="[0, 0.75, 1]"
  [initialBreakpoint]="0.75"
  #topUpModal
>
  <ng-template>
    <ion-header [translucent]="true">
      <ion-toolbar>
        <ion-title>{{ modalTitle() }}</ion-title>
        <ion-buttons slot="end">
          <ion-button fill="clear" color="primary" (click)="isModalOpen.set(false)">
            <ion-icon name="close" slot="icon-only" />
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content class="ion-padding">
      @switch (exchangeRateState().type) {
        @case ("loading") {
          <mtjr-loading />
        }
        @case ("error") {
          <mtjr-error [failure]="exchangeRateFailure()" />
        }
        @case ("success") {
          @if (!amount()) {
            <mtjr-text-field
              [field]="amountField"
              type="number"
              inputmode="decimal"
              placeholder="0"
              class="text-xl mb-4 pb-4"
            >
              <span slot="end">{{ "COMMON.CURRENCY_SYMBOL" | translate }}</span>
            </mtjr-text-field>
          }

          <ion-radio-group class="bg-medium/10 rounded-md" [value]="selectedMethod()">
            @for (topUpPaymentMethod of TopUpPaymentMethodList; track $index) {
              <ion-item
                class="rounded-xl"
                lines="none"
                button="true"
                (click)="selectedMethod.set(topUpPaymentMethod)"
                mode="md"
              >
                <ion-icon
                  slot="start"
                  [name]="paymentMethodIcons[topUpPaymentMethod]"
                  class="me-3 text-4xl rounded-xl"
                ></ion-icon>
                <ion-label class="text-primary">
                  <ion-text class="mt-3 select-none">
                    {{ "ENUMS.TopUpPaymentMethod." + topUpPaymentMethod | translate }}
                  </ion-text>
                </ion-label>
                <ion-radio slot="end" [value]="topUpPaymentMethod" mode="md"></ion-radio>
              </ion-item>
            }
          </ion-radio-group>

          <!-- Crypto -->
          <div
            class="payment-section-container"
            [class.open]="selectedMethod() === 'crypto'"
            [class.delayed]="selectedMethod() === 'crypto'"
          >
            <div class="content">
              <div class="flex items-center justify-center my-2 gap-2">
                <ion-icon name="usdt" size="large"></ion-icon>
                <ion-text class="text-medium">{{ "COMMON.OR" | translate }}</ion-text>
                <ion-icon name="usdc" size="large"></ion-icon>
                <ion-text class="text-primary text-center font-bold">
                  {{ valueInUSD().toNumber() | currency: "USD" }}
                </ion-text>
              </div>

              <div class="bg-primary/10 my-2 flex items-center justify-center gap-2 rounded-lg py-3">
                <ion-text class="text-medium">
                  {{ "WALLET.EXCHANGE_RATE" | translate }}
                </ion-text>
                <ion-text class="text-dark">
                  {{ usdExchangeRate() | vxCurrency }}
                </ion-text>
              </div>
            </div>
          </div>

          <!-- YussorPay -->
          <div
            class="payment-section-container"
            [class.open]="selectedMethod() === 'yussorPay'"
            [class.delayed]="selectedMethod() === 'yussorPay'"
          >
            <div class="content">
              <div class="px-4">
                <mtjr-text-field
                  [field]="identityCardField"
                  type="text"
                  inputmode="decimal"
                  placeholder="رقم الهوية"
                  class="text-xl"
                ></mtjr-text-field>
              </div>
            </div>
          </div>

          <!-- Moamalat -->
          <div
            class="payment-section-container"
            [class.open]="selectedMethod() === 'moamalat'"
            [class.delayed]="selectedMethod() === 'moamalat'"
          >
            <div class="content">
              <div class="bg-primary/10 my-2 flex items-center justify-center gap-2 rounded-lg py-3">
                <ion-text class="text-medium">
                  {{ "WALLET.WITH_FEE" | translate }}
                </ion-text>
                <ion-text class="text-dark">
                  {{ amountWithFee() | vxCurrency }}
                </ion-text>
              </div>
            </div>
          </div>

          @if (createPaymentIntentFailure()) {
            <div class="my-4">
              <mtjr-error-bar [failure]="createPaymentIntentFailure()" />
            </div>
          }

          <mtjr-submit-button
            class="px-3 h-12"
            [state]="createPaymentIntentState()"
            [label]="submitButtonLabel()"
            (click)="createPaymentIntent()"
            [disabled]="!amount() && amountField.invalid"
          />
        }
      }
    </ion-content>
  </ng-template>
</ion-modal>

<!-- Checkout Modal -->
<ion-modal
  [isOpen]="checkoutModalOpen()"
  (willDismiss)="checkoutModalDismiss.emit()"
  [expandToScroll]="false"
  cssClass="rounded-dialog"
  [breakpoints]="[0.0, 0.5]"
  [initialBreakpoint]="0.5"
  #checkoutModal
>
  <ng-template>
    <ion-content class="ion-padding">
      @if (selectedMethod() === "yussorPay") {
        <div class="flex flex-col items-center">
          <ion-icon name="yussor-pay" color="medium" slot="end" class="text-[196px]"></ion-icon>

          <ion-text class="mb-4">
            {{ "WALLET.YUSSOR_PAY_MESSAGE" | translate }}
          </ion-text>

          <mtjr-text-field
            [field]="otpField"
            type="text"
            inputmode="decimal"
            placeholder="رمز التأكيد"
            class="text-xl"
          ></mtjr-text-field>

          <ion-button expand="block" class="mx-4 mt-6 min-w-[200px]" (click)="confirmYussorPay()">
            {{ "COMMON.CONTINUE" | translate }}
          </ion-button>
        </div>
      }

      @if (selectedMethod() === "crypto" && checkoutUrl(); as url) {
        <div class="flex flex-col items-center">
          <mtjr-dynamic-img
            src="https://dash.wpay.ly/assets/logo-icon-text-dark.svg"
            darkSrc="https://dash.wpay.ly/assets/logo-icon-text-dark.svg"
            class="my-4 w-[180px] rounded-lg object-cover"
            [alt]="'WALLET.CHECKOUT_QR_CODE' | translate"
          ></mtjr-dynamic-img>

          <ion-text>
            {{ "WALLET.LINK_REDIRECT_MESSAGE" | translate }}
          </ion-text>

          <ion-button expand="block" class="mx-4 mt-6 min-w-[200px]" (click)="openUrl(url)">
            {{ "COMMON.CONTINUE" | translate }}
          </ion-button>

          <ion-button fill="clear" (click)="copyToClipboard(url)" size="small" class="mt-4 !min-w-[200px]">
            <ion-icon name="copy-outline" color="medium" slot="end"></ion-icon>
            <ion-text>{{ "WALLET.COPY_CHECKOUT_LINK" | translate }}</ion-text>
          </ion-button>
        </div>
      }

      @if (selectedMethod() === "moamalat") {
        <div class="flex flex-col items-center">
          <ion-icon name="moamalat-full" color="medium" slot="end" class="text-[196px]"></ion-icon>

          <ion-text>
            {{ "WALLET.LINK_REDIRECT_MESSAGE" | translate }}
          </ion-text>

          <ion-button
            expand="block"
            class="mx-4 mt-6 min-w-[200px]"
            (click)="goToMoamalat(); checkoutModalOpen.set(false); topUpModal.present()"
          >
            {{ "COMMON.CONTINUE" | translate }}
          </ion-button>
        </div>
      }

      @if (confirmFailure()) {
        <div class="my-4">
          <mtjr-error-bar [failure]="confirmFailure()" />
        </div>
      }
    </ion-content>
  </ng-template>
</ion-modal>
